// The grid: a spreadsheet over a table's records. Works like Excel and Airtable:
//  - click or drag to select; Shift extends; arrows, Tab and Enter move
//  - type to replace a cell; Enter, F2 or double-click edits it; Esc cancels
//  - choices, people and linked records open a picker; Space ticks a checkbox
//  - Ctrl/Cmd+C copies, Ctrl/Cmd+V pastes a block from Excel (extra rows become new records), Delete clears,
//    Ctrl/Cmd+D fills down, Ctrl/Cmd+Z undoes (the page keeps the history), Shift+Space opens the record
// Rows are virtualised, so a long table scrolls smoothly.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Field, Links, PersonRow, Row, Value } from "./pApi";
import { CellValue, numeric } from "./Cell";
import { ChoiceEditor, LinkEditor, LongTextEditor, PersonEditor, Popover } from "./Editors";
import { EDITABLE, TYPE_INFO, fromText, text, toInput } from "./format";

export type Item = { kind: "row"; row: Row; depth?: number } | { kind: "group"; key: string; label: ReactNode; count: number; collapsed: boolean; toggle: () => void };
export type RowAction = { label: string; run: () => void; danger?: boolean; disabled?: boolean };

type Props = {
  fields: Field[]; allFields: Field[]; items: Item[]; links: Links; people: PersonRow[]; canEdit: boolean;
  widths: Record<string, number>; onWidth?: (key: string, w: number) => void;
  onEdit: (rowId: string, values: Record<string, unknown>) => void;
  onBulk: (updates: { id: string; values: Record<string, unknown> }[], newRows?: Record<string, unknown>[]) => void;
  onAddRow?: (opts?: { after?: string | null; parentId?: string | null }) => void;
  onDeleteRows: (ids: string[]) => void;
  onOpenRow: (id: string) => void;
  onHeader?: (f: Field, el: HTMLElement) => void;
  onAddField?: (el: HTMLElement) => void;
  onAddChoice?: (f: Field, name: string) => Promise<void>;
  onUndo?: () => void;
  readOnlyKeys?: (row: Row) => string[]; // e.g. a summary task's dates
  tree?: { hasKids: Set<string>; collapsed: Set<string>; toggle: (id: string) => void };
  rowActions?: (row: Row) => RowAction[];
  fieldsOf: (tableId: string) => Field[] | undefined;
  rowHeight?: number; virtual?: boolean;
  scrollRef?: React.MutableRefObject<HTMLDivElement | null>; onScrollY?: (y: number) => void;
  sortedKeys?: string[]; filteredKeys?: string[];
  emptyHint?: ReactNode;
  onSelectRow?: (rowId: string | null) => void;
};

const HEAD = 34, GUTTER = 64, MIN_W = 80;
const defaultWidth = (f: Field) => (f.key === "__duration" ? 76 : f.primary ? 240 : f.type === "longtext" ? 260 : ["checkbox"].includes(f.type) ? 96 : ["number", "currency", "percent", "autonumber"].includes(f.type) ? 120 : ["date", "created", "updated"].includes(f.type) ? 140 : 180);
const INLINE = new Set(["text", "url", "email", "number", "currency", "percent", "date"]);
type Pos = { r: number; c: number };

export function GridView(p: Props) {
  const RH = p.rowHeight ?? 34;
  const box = useRef<HTMLDivElement | null>(null);
  const setBox = (el: HTMLDivElement | null) => { box.current = el; if (p.scrollRef) p.scrollRef.current = el; };
  const [sel, setSel] = useState<Pos | null>(null);
  const [anchor, setAnchor] = useState<Pos | null>(null);
  const [editing, setEditing] = useState<{ r: number; c: number; initial: string } | null>(null);
  const [picker, setPicker] = useState<{ r: number; c: number; el: HTMLElement } | null>(null);
  const [menu, setMenu] = useState<{ row: Row; x: number; y: number } | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [range, setRange] = useState({ top: 0, height: 800 });
  const dragging = useRef(false);

  const cols = p.fields;
  const width = (f: Field) => Math.max(MIN_W, p.widths[f.key] ?? defaultWidth(f));
  const totalW = GUTTER + cols.reduce((s, f) => s + width(f), 0) + (p.onAddField ? 48 : 0);
  const rowAt = (r: number) => { const it = p.items[r]; return it?.kind === "row" ? it.row : null; };

  useEffect(() => { p.onSelectRow?.(sel ? rowAt(sel.r)?.id ?? null : null); }, [sel?.r, p.items]); // eslint-disable-line react-hooks/exhaustive-deps
  // Keep the selection on the grid when rows come and go.
  useEffect(() => { if (sel && sel.r >= p.items.length) { setSel(null); setAnchor(null); } }, [p.items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setChecked((c) => new Set([...c].filter((id) => p.items.some((i) => i.kind === "row" && i.row.id === id)))); }, [p.items]);

  // ----- virtual window -----
  const onScroll = () => { const el = box.current; if (!el) return; setRange({ top: el.scrollTop, height: el.clientHeight }); p.onScrollY?.(el.scrollTop); };
  useEffect(() => { onScroll(); const ro = new ResizeObserver(onScroll); if (box.current) ro.observe(box.current); return () => ro.disconnect(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const virtual = p.virtual !== false && p.items.length > 120;
  const first = virtual ? Math.max(0, Math.floor((range.top - HEAD) / RH) - 15) : 0;
  const last = virtual ? Math.min(p.items.length, Math.ceil((range.top + range.height) / RH) + 15) : p.items.length;

  const rect = useMemo(() => {
    if (!sel) return null;
    const a = anchor ?? sel;
    return { r0: Math.min(a.r, sel.r), r1: Math.max(a.r, sel.r), c0: Math.min(a.c, sel.c), c1: Math.max(a.c, sel.c) };
  }, [sel, anchor]);
  const inRect = (r: number, c: number) => !!rect && r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1;

  const scrollTo = (r: number, c: number) => {
    const el = box.current; if (!el) return;
    const y = HEAD + r * RH;
    if (y < el.scrollTop + HEAD) el.scrollTop = y - HEAD;
    else if (y + RH > el.scrollTop + el.clientHeight - 34) el.scrollTop = y + RH - el.clientHeight + 34;
    let x = GUTTER; for (let i = 0; i < c; i++) x += width(cols[i]);
    const frozen = GUTTER + (cols[0] ? width(cols[0]) : 0);
    if (c > 0 && x < el.scrollLeft + frozen) el.scrollLeft = x - frozen;
    else if (x + width(cols[c]) > el.scrollLeft + el.clientWidth) el.scrollLeft = x + width(cols[c]) - el.clientWidth;
  };
  const move = (dr: number, dc: number, extend = false) => {
    const cur = sel ?? { r: 0, c: 0 };
    let r = cur.r + dr; const c = Math.max(0, Math.min(cols.length - 1, cur.c + dc));
    // Skip group headers.
    while (r >= 0 && r < p.items.length && p.items[r].kind !== "row") r += dr || 1;
    if (r < 0 || r >= p.items.length) r = cur.r;
    const next = { r, c };
    setSel(next); if (!extend) setAnchor(next);
    scrollTo(r, c);
  };

  const readOnly = (row: Row, f: Field) => !p.canEdit || !EDITABLE(f) || (p.readOnlyKeys?.(row).includes(f.key) ?? false);
  const startEdit = (r: number, c: number, initial?: string) => {
    const row = rowAt(r), f = cols[c]; if (!row || !f || readOnly(row, f)) return;
    if (f.type === "checkbox") { p.onEdit(row.id, { [f.key]: !row.values[f.key] }); return; }
    if (INLINE.has(f.type)) { setEditing({ r, c, initial: initial ?? (f.type === "date" ? String(row.values[f.key] ?? "") : rawText(f, row.values[f.key])) }); return; }
    const el = box.current?.querySelector<HTMLElement>(`[data-cell="${r}:${c}"]`);
    if (el) setPicker({ r, c, el });
  };
  const commit = (r: number, c: number, raw: string) => {
    const row = rowAt(r), f = cols[c]; setEditing(null);
    if (!row || !f) return;
    const before = rawText(f, row.values[f.key]);
    if (raw === before || (f.type === "date" && raw === String(row.values[f.key] ?? ""))) return;
    p.onEdit(row.id, { [f.key]: raw.trim() === "" ? null : raw });
  };

  // ----- clipboard -----
  const tsvOf = useCallback(() => {
    if (!rect) return "";
    const lines: string[] = [];
    for (let r = rect.r0; r <= rect.r1; r++) {
      const row = rowAt(r); if (!row) continue;
      const cells: string[] = [];
      for (let c = rect.c0; c <= rect.c1; c++) { const f = cols[c]; const s = f.type === "date" ? String(row.values[f.key] ?? "") : text(f, row.values[f.key], p.links); cells.push(/[\t\n"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s); }
      lines.push(cells.join("\t"));
    }
    return lines.join("\n");
  }, [rect, p.items, cols, p.links]); // eslint-disable-line react-hooks/exhaustive-deps
  const paste = (txt: string) => {
    if (!sel || !p.canEdit) return;
    const block = parseTsv(txt); if (!block.length) return;
    const r0 = rect?.r0 ?? sel.r, c0 = rect?.c0 ?? sel.c;
    const updates = new Map<string, Record<string, unknown>>();
    const newRows: Record<string, unknown>[] = [];
    // One value pasted over a selection fills it, like Excel.
    const single = block.length === 1 && block[0].length === 1 && rect && (rect.r1 > rect.r0 || rect.c1 > rect.c0);
    const rowsN = single ? rect!.r1 - rect!.r0 + 1 : block.length, colsN = single ? rect!.c1 - rect!.c0 + 1 : block[0].length;
    let r = r0;
    for (let i = 0; i < rowsN; i++) {
      while (r < p.items.length && p.items[r].kind !== "row") r++;
      const row = rowAt(r);
      const vals: Record<string, unknown> = {};
      for (let j = 0; j < colsN; j++) {
        const f = cols[c0 + j]; if (!f) break;
        const s = single ? block[0][0] : block[i][j] ?? "";
        if (row ? readOnly(row, f) : !EDITABLE(f)) continue;
        vals[f.key] = f.type === "checkbox" ? /^(true|yes|y|x|1|✓)$/i.test(s.trim()) : fromText(f, s, p.links, p.people);
      }
      if (row) { if (Object.keys(vals).length) updates.set(row.id, vals); }
      else if (!single && Object.keys(vals).length) newRows.push(vals);
      r++;
    }
    if (updates.size || newRows.length) p.onBulk([...updates].map(([id, values]) => ({ id, values })), newRows);
  };
  const clear = () => {
    if (!rect || !p.canEdit) return;
    const updates: { id: string; values: Record<string, unknown> }[] = [];
    for (let r = rect.r0; r <= rect.r1; r++) {
      const row = rowAt(r); if (!row) continue;
      const vals: Record<string, unknown> = {};
      for (let c = rect.c0; c <= rect.c1; c++) { const f = cols[c]; if (!readOnly(row, f) && row.values[f.key] !== undefined) vals[f.key] = f.type === "checkbox" ? false : null; }
      if (Object.keys(vals).length) updates.push({ id: row.id, values: vals });
    }
    if (updates.length) p.onBulk(updates);
  };
  const fillDown = () => {
    if (!rect || rect.r1 === rect.r0 || !p.canEdit) return;
    const top = rowAt(rect.r0); if (!top) return;
    const updates: { id: string; values: Record<string, unknown> }[] = [];
    for (let r = rect.r0 + 1; r <= rect.r1; r++) {
      const row = rowAt(r); if (!row) continue;
      const vals: Record<string, unknown> = {};
      for (let c = rect.c0; c <= rect.c1; c++) { const f = cols[c]; if (!readOnly(row, f)) vals[f.key] = toInput(f, top.values[f.key]) ?? (f.type === "checkbox" ? false : null); }
      updates.push({ id: row.id, values: vals });
    }
    p.onBulk(updates);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (editing || picker) return;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); p.onUndo?.(); return; }
    if (!sel) { if (["ArrowDown", "ArrowRight", "Enter", "Tab"].includes(e.key) && p.items.length) { e.preventDefault(); move(0, 0); } return; }
    if (mod && e.key.toLowerCase() === "d") { e.preventDefault(); fillDown(); return; }
    if (mod && e.key.toLowerCase() === "a") { e.preventDefault(); setAnchor({ r: 0, c: 0 }); setSel({ r: p.items.length - 1, c: cols.length - 1 }); return; }
    if (mod) return; // copy and paste come through their own events
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); move(1, 0, e.shiftKey); return;
      case "ArrowUp": e.preventDefault(); move(-1, 0, e.shiftKey); return;
      case "ArrowRight": e.preventDefault(); move(0, 1, e.shiftKey); return;
      case "ArrowLeft": e.preventDefault(); move(0, -1, e.shiftKey); return;
      case "Tab": e.preventDefault(); move(0, e.shiftKey ? -1 : 1); return;
      case "Home": e.preventDefault(); move(0, -cols.length); return;
      case "End": e.preventDefault(); move(0, cols.length); return;
      case "PageDown": e.preventDefault(); move(Math.floor((box.current?.clientHeight ?? 400) / RH), 0, e.shiftKey); return;
      case "PageUp": e.preventDefault(); move(-Math.floor((box.current?.clientHeight ?? 400) / RH), 0, e.shiftKey); return;
      case "Enter": case "F2": e.preventDefault(); startEdit(sel.r, sel.c); return;
      case "Escape": setAnchor(sel); return;
      case "Delete": case "Backspace": e.preventDefault(); clear(); return;
      case " ":
        e.preventDefault();
        if (e.shiftKey) { const row = rowAt(sel.r); if (row) p.onOpenRow(row.id); return; }
        if (cols[sel.c]?.type === "checkbox") startEdit(sel.r, sel.c); else startEdit(sel.r, sel.c, " ");
        return;
    }
    if (e.key.length === 1 && !e.altKey) {
      const f = cols[sel.c];
      if (f && INLINE.has(f.type) && f.type !== "date") { e.preventDefault(); startEdit(sel.r, sel.c, e.key); }
      else if (f) { e.preventDefault(); startEdit(sel.r, sel.c); }
    }
  };

  // ----- column resize -----
  const resize = (f: Field, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX, w0 = width(f);
    let w = w0;
    const head = (e.currentTarget as HTMLElement).parentElement!;
    const mm = (ev: MouseEvent) => { w = Math.max(MIN_W, w0 + ev.clientX - x0); head.style.width = `${w}px`; document.querySelectorAll<HTMLElement>(`[data-col="${f.key}"]`).forEach((el) => { el.style.width = `${w}px`; }); };
    const up = () => { document.removeEventListener("mousemove", mm); document.removeEventListener("mouseup", up); document.body.style.cursor = ""; if (w !== w0) p.onWidth?.(f.key, Math.round(w)); };
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", mm); document.addEventListener("mouseup", up);
  };

  useEffect(() => { const up = () => { dragging.current = false; }; document.addEventListener("mouseup", up); return () => document.removeEventListener("mouseup", up); }, []);

  const sums = useMemo(() => {
    const out: Record<string, number> = {};
    for (const f of cols) if (numeric(f) && f.type !== "autonumber" && !f.key.startsWith("__")) {
      let s = 0, n = 0; for (const it of p.items) if (it.kind === "row") { const v = it.row.values[f.key]; if (typeof v === "number") { s += v; n++; } }
      if (n) out[f.key] = s;
    }
    return out;
  }, [p.items, cols]);
  const rowCount = p.items.filter((i) => i.kind === "row").length;
  const rowNo = useMemo(() => { let n = 0; return p.items.map((it) => (it.kind === "row" ? ++n : 0)); }, [p.items]);
  const checkedIds = [...checked];

  const cellAt = (row: Row, r: number, c: number, f: Field, depth: number) => {
    const ro = readOnly(row, f);
    const isSel = sel?.r === r && sel?.c === c;
    const isEd = editing?.r === r && editing?.c === c;
    const v = row.values[f.key] as Value | undefined;
    return (
      <div key={f.key} data-cell={`${r}:${c}`} data-col={f.key} role="gridcell" aria-selected={inRect(r, c)}
        className={`pg-cell${c === 0 ? " pg-frozen" : ""}${numeric(f) ? " num" : ""}${isSel ? " cur" : inRect(r, c) ? " in" : ""}${ro && EDITABLE(f) && p.canEdit ? " ro" : ""}${f.type === "checkbox" ? " cb" : ""}`}
        style={{ width: width(f), left: c === 0 ? GUTTER : undefined }}
        onMouseDown={(e) => { if (e.button !== 0) return; const pos = { r, c }; setSel(pos); if (!e.shiftKey) setAnchor(pos); dragging.current = true; setEditing(null); }}
        onMouseEnter={() => { if (dragging.current) setSel({ r, c }); }}
        onDoubleClick={() => (ro ? p.onOpenRow(row.id) : startEdit(r, c))}>
        {c === 0 && (p.tree ? <span className="pg-indent" style={{ width: depth * 18 }} /> : null)}
        {c === 0 && p.tree && (p.tree.hasKids.has(row.id) ? <button className="pg-caret" aria-label={p.tree.collapsed.has(row.id) ? "Show subtasks" : "Hide subtasks"} onMouseDown={(e) => e.stopPropagation()} onClick={() => p.tree!.toggle(row.id)}>{p.tree.collapsed.has(row.id) ? "▸" : "▾"}</button> : <span className="pg-caret-sp" />)}
        {isEd ? (
          <input autoFocus className={`pg-in${numeric(f) ? " num" : ""}`} type={f.type === "date" ? "date" : "text"} defaultValue={editing!.initial}
            onFocus={(e) => { if (f.type !== "date") { const el = e.currentTarget; const n = el.value.length; el.setSelectionRange(n, n); } }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") { e.preventDefault(); commit(r, c, e.currentTarget.value); move(1, 0); box.current?.focus(); }
              else if (e.key === "Tab") { e.preventDefault(); commit(r, c, e.currentTarget.value); move(0, e.shiftKey ? -1 : 1); box.current?.focus(); }
              else if (e.key === "Escape") { e.preventDefault(); setEditing(null); box.current?.focus(); }
            }}
            onBlur={(e) => { if (editing) commit(r, c, e.currentTarget.value); }} />
        ) : (
          <span className={`pg-v${f.primary ? " primary" : ""}${depth === 0 && p.tree?.hasKids.has(row.id) && c === 0 ? " summary" : ""}`}
            onMouseDown={f.type === "checkbox" && !ro ? (e) => { e.stopPropagation(); setSel({ r, c }); setAnchor({ r, c }); p.onEdit(row.id, { [f.key]: !v }); } : undefined}>
            <CellValue f={f} v={v} links={p.links} allFields={p.allFields} fieldsOf={p.fieldsOf} />
          </span>
        )}
        {c === 0 && <button className="pg-open" title="Open record (Shift+Space)" aria-label="Open record" onMouseDown={(e) => e.stopPropagation()} onClick={() => p.onOpenRow(row.id)}>⤢</button>}
      </div>
    );
  };

  const pickerCell = picker ? { row: rowAt(picker.r), f: cols[picker.c] } : null;

  return (
    <div className="pg-shell">
      {checkedIds.length > 0 && p.canEdit && (
        <div className="pg-bulk" role="toolbar">
          <span>{checkedIds.length} selected</span>
          <button className="btn" onClick={() => { p.onDeleteRows(checkedIds); setChecked(new Set()); }}>Delete {checkedIds.length === 1 ? "record" : `${checkedIds.length} records`}</button>
          <button className="btn" onClick={() => setChecked(new Set())}>Clear</button>
        </div>
      )}
      <div ref={setBox} className="pg" tabIndex={0} role="grid" aria-rowcount={rowCount} aria-colcount={cols.length} onScroll={onScroll} onKeyDown={onKey}
        onCopy={(e) => { if (editing || !rect) return; e.preventDefault(); e.clipboardData.setData("text/plain", tsvOf()); }}
        onPaste={(e) => { if (editing) return; e.preventDefault(); paste(e.clipboardData.getData("text/plain")); }}>
        <div className="pg-inner" style={{ width: totalW }}>
          <div className="pg-head" role="row" style={{ height: HEAD }}>
            <div className="pg-gutter pg-frozen-g" style={{ width: GUTTER }}>
              {p.canEdit && <input type="checkbox" aria-label="Select all" checked={rowCount > 0 && checked.size === rowCount} onChange={(e) => setChecked(e.target.checked ? new Set(p.items.flatMap((i) => (i.kind === "row" ? [i.row.id] : []))) : new Set())} />}
            </div>
            {cols.map((f, c) => (
              <div key={f.key} data-col={f.key} role="columnheader" className={`pg-hc${c === 0 ? " pg-frozen" : ""}`} style={{ width: width(f), left: c === 0 ? GUTTER : undefined }}>
                <button className="pg-hb" onClick={(e) => p.onHeader?.(f, e.currentTarget)} title={`${f.name} · ${TYPE_INFO[f.type].label}${f.issue ? ` · ${f.issue}` : ""}${f.description ? `\n${f.description}` : ""}`}>
                  <span className="pg-ti" aria-hidden="true">{TYPE_INFO[f.type].icon}</span>
                  <span className="pg-hn">{f.name}</span>
                  {f.issue && <span className="pg-warn" aria-label={f.issue}>!</span>}
                  {p.sortedKeys?.includes(f.key) && <span className="pg-mark" aria-label="Sorted">↕</span>}
                  {p.filteredKeys?.includes(f.key) && <span className="pg-mark" aria-label="Filtered">⏷</span>}
                </button>
                {p.onWidth && <span className="pg-rs" onMouseDown={(e) => resize(f, e)} aria-hidden="true" />}
              </div>
            ))}
            {p.onAddField && <div className="pg-hc pg-add"><button className="pg-hb" aria-label="Add a field" title="Add a field" onClick={(e) => p.onAddField!(e.currentTarget)}>+</button></div>}
          </div>

          {virtual && <div style={{ height: first * RH }} />}
          {p.items.slice(first, last).map((it, i) => {
            const r = first + i;
            if (it.kind === "group") return (
              <div key={`g:${it.key}`} className="pg-group" style={{ height: RH }} onClick={it.toggle}>
                <span className="pg-frozen-g pg-gl" style={{ paddingLeft: 12 }}><span className="pg-caret">{it.collapsed ? "▸" : "▾"}</span>{it.label}<span className="muted">{it.count}</span></span>
              </div>
            );
            const row = it.row;
            return (
              <div key={row.id} role="row" className={`pg-row${checked.has(row.id) ? " checked" : ""}${p.tree?.hasKids.has(row.id) ? " sum" : ""}`} style={{ height: RH }}
                onContextMenu={(e) => { e.preventDefault(); setMenu({ row, x: e.clientX, y: e.clientY }); }}>
                <div className="pg-gutter pg-frozen-g" style={{ width: GUTTER }}>
                  {p.canEdit && <input type="checkbox" aria-label={`Select record ${row.number}`} checked={checked.has(row.id)} onChange={() => setChecked((s) => { const n = new Set(s); if (n.has(row.id)) n.delete(row.id); else n.add(row.id); return n; })} />}
                  <span className="pg-num">{rowNo[r]}</span>
                </div>
                {cols.map((f, c) => cellAt(row, r, c, f, it.depth ?? 0))}
              </div>
            );
          })}
          {virtual && <div style={{ height: Math.max(0, p.items.length - last) * RH }} />}

          {p.canEdit && p.onAddRow && (
            <div className="pg-addrow" style={{ height: RH }}>
              <button className="pg-frozen-g" onClick={() => p.onAddRow!()}><span aria-hidden="true">+</span> Add record</button>
            </div>
          )}
          {!p.items.length && p.emptyHint && <div className="pg-empty">{p.emptyHint}</div>}

          <div className="pg-foot" role="row">
            <div className="pg-gutter pg-frozen-g" style={{ width: GUTTER }} />
            {cols.map((f, c) => (
              <div key={f.key} data-col={f.key} className={`pg-fc${c === 0 ? " pg-frozen" : ""}${numeric(f) ? " num" : ""}`} style={{ width: width(f), left: c === 0 ? GUTTER : undefined }}>
                {c === 0 ? `${rowCount.toLocaleString()} ${rowCount === 1 ? "record" : "records"}` : sums[f.key] !== undefined ? <span title="Sum">Σ {text(f.type === "formula" || f.type === "rollup" ? { ...f, type: "number" } : f, sums[f.key])}</span> : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      {editing === null && picker && pickerCell?.row && pickerCell.f && (() => {
        const { row, f } = pickerCell as { row: Row; f: Field };
        const close = () => { setPicker(null); box.current?.focus(); };
        const set = (v: unknown) => p.onEdit(row.id, { [f.key]: v });
        if (f.type === "select" || f.type === "multiselect") return <ChoiceEditor f={f} value={row.values[f.key]} anchor={picker.el} onChange={set} onClose={close} onAddChoice={p.onAddChoice ? (n) => p.onAddChoice!(f, n) : undefined} />;
        if (f.type === "person") return <PersonEditor value={row.values[f.key]} anchor={picker.el} people={p.people} onChange={set} onClose={close} />;
        if (f.type === "link") return <LinkEditor f={f} value={row.values[f.key]} anchor={picker.el} links={p.links} selfId={row.id} onChange={set} onClose={close} />;
        if (f.type === "longtext") return <LongTextEditor value={row.values[f.key]} anchor={picker.el} onChange={set} onClose={close} />;
        return null;
      })()}

      {menu && (
        <Popover anchor={new DOMRect(menu.x, menu.y, 0, 0)} onClose={() => setMenu(null)} width={220} className="pv-menu">
          <button onClick={() => { p.onOpenRow(menu.row.id); setMenu(null); }}>Open record</button>
          {p.canEdit && p.onAddRow && <button onClick={() => { const i = p.items.findIndex((x) => x.kind === "row" && x.row.id === menu.row.id); const prev = p.items.slice(0, i).reverse().find((x) => x.kind === "row"); p.onAddRow!({ after: prev && prev.kind === "row" ? prev.row.id : null, parentId: menu.row.parentId }); setMenu(null); }}>Insert record above</button>}
          {p.canEdit && p.onAddRow && <button onClick={() => { p.onAddRow!({ after: menu.row.id, parentId: menu.row.parentId }); setMenu(null); }}>Insert record below</button>}
          {(p.rowActions?.(menu.row) ?? []).map((a) => <button key={a.label} disabled={a.disabled} className={a.danger ? "danger" : ""} onClick={() => { a.run(); setMenu(null); }}>{a.label}</button>)}
          {p.canEdit && <button className="danger" onClick={() => { p.onDeleteRows([menu.row.id]); setMenu(null); }}>Delete record</button>}
        </Popover>
      )}
    </div>
  );
}

/** A value as editable text (numbers without formatting, so 1250 not $1,250.00). */
function rawText(f: Field, v: Value | undefined): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") return v;
  return "";
}

/** Tab-separated text from Excel or Sheets, including quoted cells with tabs or line breaks. */
export function parseTsv(s: string): string[][] {
  const out: string[][] = []; let row: string[] = [], cell = "", q = false;
  const t = s.replace(/\r\n?/g, "\n").replace(/\n$/, "");
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (q) { if (ch === '"') { if (t[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch; }
    else if (ch === '"' && cell === "") q = true;
    else if (ch === "\t") { row.push(cell); cell = ""; }
    else if (ch === "\n") { row.push(cell); out.push(row); row = []; cell = ""; }
    else cell += ch;
  }
  row.push(cell); out.push(row);
  return out;
}
